import { Number } from "components/Number";
import { device, mq } from "lib/media-query";
import React from "react";
import { DL_FILTER_EVENT, DL_FILTER_ACTION_ON, DL_FILTER_ACTION_OFF } from "helpers/constants";
import styled, { css } from "styled-components";
import BedrijfswagenIcon from "svg/car-bedrijfswagen.svg?sprite";
import CabrioletIcon from "svg/car-cabriolet.svg?sprite";
import CamperIcon from "svg/car-camper.svg?sprite";
import CoupeIcon from "svg/car-coupe.svg?sprite";
import HatchbackIcon from "svg/car-hatchback.svg?sprite";
import StationwagenIcon from "svg/car-stationwagen.svg?sprite";
import PickupIcon from "svg/car-pick-up.svg?sprite";
import SedanIcon from "svg/car-sedan.svg?sprite";
import MpvIcon from "svg/car-mpv.svg?sprite";
import SuvIcon from "svg/car-suv.svg?sprite";
import PersonenbusIcon from "svg/car-personenbus.svg?sprite";
import OverigIcon from "svg/car-other.svg?sprite";
import EnergieLabelAIcon from "svg/EL_A.svg";
import EnergieLabelBIcon from "svg/EL_B.svg";
import EnergieLabelCIcon from "svg/EL_C.svg";
import EnergieLabelDIcon from "svg/EL_D.svg";
import EnergieLabelEIcon from "svg/EL_E.svg";
import EnergieLabelFIcon from "svg/EL_F.svg";
import EnergieLabelGIcon from "svg/EL_G.svg";
import { datalayerWrapper } from "lib/analytics/datalayerHelpers";

const buttonGroupImagesMap = {
    BEDRIJFSWAGEN: BedrijfswagenIcon,
    CABRIOLET: CabrioletIcon,
    CAMPER: CamperIcon,
    COUPE: CoupeIcon,
    HATCHBACK: HatchbackIcon,
    STATIONWAGEN: StationwagenIcon,
    PICK_UP: PickupIcon,
    SEDAN: SedanIcon,
    MPV: MpvIcon,
    SUV: SuvIcon,
    PERSONENBUS: PersonenbusIcon,
    OVERIG: OverigIcon,
    A: EnergieLabelAIcon,
    B: EnergieLabelBIcon,
    C: EnergieLabelCIcon,
    D: EnergieLabelDIcon,
    E: EnergieLabelEIcon,
    F: EnergieLabelFIcon,
    G: EnergieLabelGIcon
};

const Group = styled.div`
    display: grid;
    grid-template-columns: repeat(${({ columns }) => columns}, 1fr);
    padding: 0;
    margin: 0;
    ${({ filterId, columns }) =>
        filterId === "filter-afstand-radius" && columns > 2 && `height: 40px;`};

    ${mq.to(device.TABLET)`
     grid-template-columns: repeat(2, 1fr);
  `};
`;

const Button = styled.button`
    margin-top: -1px;
    margin-left: -1px;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.tertiary[800]};
    flex: 0 50%;
    padding: 8px;
    font-size: 13px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    border: 1px solid ${({ theme }) => theme.colors.tertiary[600]};
    word-break: break-word;
    background-color: white;
    z-index: 10;
    line-height: 1.25;

    svg {
        height: 40px;
        fill: ${({ theme }) => theme.colors.tertiary[700]};
        pointer-events: none;
    }

    &:nth-child(1) {
        border-top-left-radius: 6px;
    }

    &:nth-last-child(1) {
        border-bottom-right-radius: 6px;
    }

    &:nth-child(${({ columns }) => columns}) {
        border-top-right-radius: 6px;
    }

    &:nth-last-child(${({ columns }) => columns}) {
        border-bottom-left-radius: 6px;
    }

    &:focus {
        z-index: 11;
        box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.secondary[700]};
        box-sizing: border-box;
    }

    &:disabled {
        border: 1px solid ${({ theme }) => theme.colors.breathing};
        color: ${({ theme }) => theme.colors.breathing};
        cursor: auto;
        pointer-events: none;
    }

    ${({ selected, theme }) =>
        selected
            ? css`
                  background-color: ${theme.colors.secondary[100]};
                  border-color: ${theme.colors.secondary[700]};
                  color: ${theme.colors.tertiary[800]};
                  z-index: 11;

                  svg {
                      fill: ${theme.colors.secondary[700]};
                  }

                  &:disabled {
                      background-color: ${theme.colors.white};
                      border: 1px solid ${theme.colors.breathing};
                      color: ${theme.colors.breathing};
                  }
              `
            : ""}

    ${({ empty, theme }) =>
        empty
            ? css`
                  border: 1px solid ${theme.colors.breathing};
                  cursor: auto;
                  pointer-events: none;
              `
            : ""}

  ${({ dark, theme }) =>
        dark
            ? css`
                  border: 1px solid ${theme.colors.tertiary[600]};
              `
            : ""}


  ${(props) => mq.with(props).to(device.TABLET)`
      &:nth-child(${({ columns }) => columns}) {
        border-top-right-radius: 0;
      }

      &:nth-last-child(${({ columns }) => columns}) {
        border-bottom-left-radius: 0;
      }

      &:nth-child(2) {
        border-top-right-radius: 6px;
      }

      &:nth-last-child(2) {
        border-bottom-left-radius: 6px;
      }
  `};

    ${({ condensed }) =>
        condensed
            ? css`
                  svg {
                      height: 18px;
                  }
              `
            : ""}
`;

const ScaledContainer = styled.div`
    transform: ${({ scale }) => `scale(${scale})`};
`;

const Count = styled.div`
    color: ${({ theme }) => theme.colors.tertiary[700]};
`;

const Image = ({ id, scale }) => {
    const Component = buttonGroupImagesMap[id];
    if (scale && Component) {
        return (
            <ScaledContainer scale={scale}>
                <Component />
            </ScaledContainer>
        );
    }

    if (Component) {
        return <Component />;
    }

    return null;
};

function pushToDataLayer(key, value, action) {
    datalayerWrapper({
        event: DL_FILTER_EVENT,
        filter_action: action,
        filter_name: key,
        filter_value: value.toLowerCase()
    });
}

const FilterButtonGroup = ({
    filterId,
    value,
    options,
    handleChange,
    datalayerKey,
    classes = "",
    isDisabled = false,
    isSingle = false,
    columns = 2,
    condensed,
    dataTestId = "filter-button-group",
    scale
}) => {
    if (!options || options.length === 0) {
        return null;
    }

    const toggle = (changedId, changedValue) => {
        if (value.includes(changedId)) {
            handleChange(
                filterId,
                value.filter((id) => id !== changedId)
            );
            pushToDataLayer(datalayerKey, changedValue, DL_FILTER_ACTION_OFF);
        } else {
            handleChange(filterId, isSingle ? [changedId] : [...value, changedId]);
            pushToDataLayer(datalayerKey, changedValue, DL_FILTER_ACTION_ON);
        }
    };

    const removeSpaceFromName = (name = "") => name.replace(/[^0-9a-zA-Z:,]+/, "");

    return (
        <Group className={classes} filterId={filterId} columns={columns} data-testid={dataTestId}>
            {options.map(({ id, displayName, aantal, naam }) => (
                <Button
                    key={`${id}`}
                    type="button"
                    onClick={() => toggle(id, naam || displayName)}
                    disabled={isDisabled}
                    selected={value.includes(id)}
                    columns={columns}
                    condensed={condensed}
                    data-testid={`button-${removeSpaceFromName(naam)}`}
                >
                    <Image id={id} scale={scale} />
                    {displayName}
                    {aantal !== undefined ? (
                        <Count>
                            (<Number value={aantal} />)
                        </Count>
                    ) : null}
                </Button>
            ))}

            {/* make sure it's an even number of buttons */}
            {new Array(columns - (options.length % columns || columns)).fill(0).map((_, i) => (
                <Button key={`${i.toString()}`} dark={isDisabled} disabled empty />
            ))}
        </Group>
    );
};

export default FilterButtonGroup;
